import React, { useEffect, useState } from "react";
import {
  DEFAULT_Z_SCORE_YEARS,
  Z_SCORE_YEARS_MAX,
  Z_SCORE_YEARS_MIN,
  formatZScoreYears,
  getZScoreIndicatorLabel,
  getZScoreYearsForIndicator,
  normalizeZScoreIndicatorKey,
  parseZScoreYearsInput,
  Z_SCORE_BASE_LABELS,
} from "../indicators/zScoreConfig";

const BRAND = {
  bg: "#0a0a0f",
  panel: "#1a1a24",
  border: "#2f2f34",
  text: "#e8e8ec",
  muted: "#9ca3af",
  accent: "#6366f1",
};

const btn = {
  background: BRAND.panel,
  color: BRAND.text,
  border: `1px solid ${BRAND.border}`,
  borderRadius: 8,
  padding: "8px 14px",
  cursor: "pointer",
};

const btnPrimary = {
  ...btn,
  background: BRAND.accent,
  border: `1px solid ${BRAND.accent}`,
  color: "#fff",
};

export default function ZScoreYearsModal({
  open,
  onClose,
  indicatorKey,
  yearsMap,
  onConfirm,
}) {
  const base = normalizeZScoreIndicatorKey(indicatorKey);
  const saved = getZScoreYearsForIndicator(indicatorKey, yearsMap);
  const [input, setInput] = useState(String(saved));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setInput(String(getZScoreYearsForIndicator(indicatorKey, yearsMap)));
    setError("");
  }, [open, indicatorKey, yearsMap]);

  if (!open || !base) return null;

  const handleConfirm = () => {
    const parsed = parseZScoreYearsInput(input);
    if (parsed == null) {
      setError(`Enter a number from ${Z_SCORE_YEARS_MIN} to ${Z_SCORE_YEARS_MAX} (max 2 decimal places).`);
      return;
    }
    onConfirm(base, parsed);
  };

  const preview = parseZScoreYearsInput(input);
  const metricLabel = Z_SCORE_BASE_LABELS[base] || base;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Above StockChart Indicators modal (zIndex 11000) and TradingView modals (50)
        zIndex: 12000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 480,
          maxWidth: "94vw",
          background: BRAND.panel,
          border: `1px solid ${BRAND.border}`,
          borderRadius: 16,
          padding: 20,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8, color: BRAND.text }}>
          Z-score lookback
        </div>
        <div style={{ fontSize: 14, color: BRAND.muted, marginBottom: 16, lineHeight: 1.5 }}>
          Choose how many <strong style={{ color: BRAND.text }}>calendar years</strong> of history
          to use for the rolling mean and standard deviation of <strong style={{ color: BRAND.text }}>{metricLabel}</strong>.
          Decimals are allowed (e.g. 0.23 or 3.45).
        </div>

        <label style={{ display: "block", fontSize: 13, color: BRAND.muted, marginBottom: 6 }}>
          Lookback (years)
        </label>
        <input
          type="number"
          min={Z_SCORE_YEARS_MIN}
          max={Z_SCORE_YEARS_MAX}
          step={0.01}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleConfirm();
          }}
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: BRAND.bg,
            color: BRAND.text,
            border: `1px solid ${error ? "#ef4444" : BRAND.border}`,
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 16,
            marginBottom: error ? 8 : 12,
          }}
          autoFocus
        />
        {error ? (
          <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{error}</div>
        ) : (
          <div style={{ fontSize: 13, color: BRAND.muted, marginBottom: 16 }}>
            {preview != null ? (
              <>
                Preview: <span style={{ color: BRAND.text }}>{getZScoreIndicatorLabel(base, preview)}</span>
                {" · "}
                Default is {formatZScoreYears(DEFAULT_Z_SCORE_YEARS)}
              </>
            ) : (
              <>Default is {formatZScoreYears(DEFAULT_Z_SCORE_YEARS)}</>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" style={btn} onClick={onClose}>
            Cancel
          </button>
          <button type="button" style={btnPrimary} onClick={handleConfirm}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
